import { Box } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { JSX } from "react";

/**
 * A pill toggle for the Change Log type filter — an optional colour dot + label
 * + optional count, with a pressed state. Extracted from `ChangelogPanel.tsx`.
 */
export function TypeChip({
  label,
  dot,
  count,
  active,
  onClick,
}: {
  label: string;
  dot?: (t: Theme) => string;
  count?: number;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={active}
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: 999,
        border: 1,
        px: 1.25,
        py: 0.5,
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        font: "inherit",
        transition: (t) => t.transitions.create(["background-color", "border-color"], { duration: 150 }),
        ...(active
          ? {
              borderColor: "primary.main",
              bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
              color: "text.primary",
            }
          : {
              borderColor: "divider",
              bgcolor: "transparent",
              color: "text.disabled",
              "&:hover": { bgcolor: "surfaceSoft" },
            }),
        "&:focus-visible": {
          outline: (t) => `2px solid ${alpha(t.palette.primary.main, 0.6)}`,
          outlineOffset: 2,
        },
      }}
    >
      {dot && (
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dot }} />
      )}
      {label}
      {count != null && (
        <Box component="span" sx={{ color: "text.disabled" }}>
          {count}
        </Box>
      )}
    </Box>
  );
}
